<template>
  <nav
    class="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-1"
    :aria-label="I18n.t('common.pagination.label')"
  >
    <SfButtonDs
      variant="tertiary"
      size="sm"
      icon-before="chevron-left"
      :disabled="currentPage === 1"
      @click="previousPage"
    >
      {{ I18n.t('common.pagination.previous') }}
    </SfButtonDs>
    <SfButtonDs
      v-if="startingAroundPage > 1"
      variant="tertiary"
      size="sm"
      class="tw-min-w-6"
      @click="changePage(1)"
    >
      1
    </SfButtonDs>
    <span v-if="startingAroundPage > 2" :class="ELLIPSIS_CLASSES"
      >&hellip;</span
    >
    <template v-if="startingAroundPage < currentPage">
      <SfButtonDs
        v-for="i in currentPage - startingAroundPage"
        :key="i + startingAroundPage - 1"
        variant="tertiary"
        size="sm"
        class="tw-min-w-6"
        @click="changePage(i + startingAroundPage - 1)"
      >
        {{ i + startingAroundPage - 1 }}
      </SfButtonDs>
    </template>
    <SfButtonDs
      variant="secondary"
      size="sm"
      class="tw-min-w-6"
      aria-current="page"
    >
      {{ currentPage }}
    </SfButtonDs>
    <template v-if="endingAroundPage > currentPage">
      <SfButtonDs
        v-for="i in endingAroundPage - currentPage"
        :key="i + currentPage"
        variant="tertiary"
        size="sm"
        class="tw-min-w-6"
        @click="changePage(i + currentPage)"
      >
        {{ i + currentPage }}
      </SfButtonDs>
    </template>
    <span v-if="endingAroundPage < totalPages - 1" :class="ELLIPSIS_CLASSES">
      &hellip;
    </span>
    <SfButtonDs
      v-if="endingAroundPage < totalPages"
      variant="tertiary"
      size="sm"
      class="tw-min-w-6"
      @click="changePage(totalPages)"
    >
      {{ totalPages }}
    </SfButtonDs>
    <SfButtonDs
      variant="tertiary"
      size="sm"
      icon-after="chevron-right"
      :disabled="currentPage === totalPages"
      @click="nextPage"
    >
      {{ I18n.t('common.pagination.next') }}
    </SfButtonDs>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { SfButtonDs } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({ name: 'SfPaginationDs' })

interface Props {
  currentPage: number
  totalPages: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ 'change-page': [number] }>()

const I18n = useI18n()

const ELLIPSIS_CLASSES = ['tw-px-1', 'tw-text-xs', 'tw-text-sf-slate']

const startingAroundPage = computed(() => Math.max(1, props.currentPage - 2))
const endingAroundPage = computed(() =>
  Math.min(props.totalPages, props.currentPage + 2)
)

function changePage(page: number) {
  emit('change-page', page)
}

function previousPage() {
  if (props.currentPage > 1) {
    changePage(props.currentPage - 1)
  }
}

function nextPage() {
  if (props.currentPage < props.totalPages) {
    changePage(props.currentPage + 1)
  }
}
</script>
